// feature/eva-litisoft — the operator "pulse": one place that answers, at a
// glance, "is Eva ready, what's ready, what's running, what happened." Shared by
// the hub header (compact) and the Resumen tab (full). Light poll (12s) so the
// header status stays live without competing with Monitor's 5s detail poll.
import { useCallback, useEffect, useState } from "react";
import { bot } from "../../../api/endpoints";

const ACTIVE = ["requested", "claimed", "running", "paused"];
const WORKER_FRESH_MS = 2 * 60 * 1000; // "conectado" if seen in the last 2 min

export function usePulse(pollMs = 12000) {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [recon, runsR, workersR, credR] = await Promise.allSettled([
        bot.reconciliation(),
        bot.runs(),
        bot.workers(),
        bot.credentialsStatus(),
      ]);
      const summary = recon.status === "fulfilled" ? (recon.value.data.summary || {}) : {};
      const runs = runsR.status === "fulfilled" ? (runsR.value.data.runs || []) : [];
      const workers = workersR.status === "fulfilled" ? (workersR.value.data.workers || []) : [];
      const cred = credR.status === "fulfilled" ? (credR.value.data || {}) : {};

      const activeRun = runs.find((r) => ACTIVE.includes(r.status)) || null;
      const pastRuns = runs.filter((r) => ["done", "cancelled", "failed"].includes(r.status));
      const lastRun = pastRuns[0] || null; // runs come newest-first
      const liveWorker = workers.find(
        (w) => !w.revoked && w.last_seen_at && (Date.now() - new Date(w.last_seen_at).getTime()) < WORKER_FRESH_MS
      );
      const pairedWorker = workers.find((w) => !w.revoked) || null;

      const credsReady = !!(cred?.previsora_user?.set && cred?.previsora_pass?.set && cred?.vault_configured !== false);

      setPulse({
        eligible: summary.eligible ?? 0,
        ambiguous: summary.ambiguous_many ?? 0,
        missingHechos: summary.blocked_missing_hechos ?? 0,
        totalPending: summary.total ?? 0,
        activeRun,
        lastRun,
        worker: { live: !!liveWorker, paired: pairedWorker, instance: liveWorker || pairedWorker || null },
        credsReady,
        vaultConfigured: cred?.vault_configured !== false,
      });
    } catch {
      setPulse((p) => p || {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  return { pulse, loading, reload: load };
}
